import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, FileText, Clock, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { requestAPI, partAPI } from '@/lib/api';
import { cn, formatDate, getStatusColor, getUrgencyBorder, truncate } from '@/lib/utils';
import { toast } from 'sonner';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'requested', label: 'Requested' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'ordered', label: 'Ordered' },
];

const urgencyOptions = [
  { value: 'all', label: 'All Urgencies' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const RequestCard = ({ request }) => {
  const navigate = useNavigate();

  return (
    <Card 
      className={cn(
        "border-l-4 cursor-pointer hover:shadow-md transition-shadow",
        getUrgencyBorder(request.urgency)
      )}
      onClick={() => navigate(`/requests/${request.id}`)}
      data-testid={`request-card-${request.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-sm font-semibold text-slate-900">
                {request.request_number}
              </span>
              <Badge className={cn("text-xs", getStatusColor(request.status))}>
                {request.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-slate-700 font-medium truncate">
              {request.part_description}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span>Qty: {request.quantity}</span>
              <span>By: {request.requester_name}</span>
              <span>{request.branch_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Badge variant="outline" className={cn("text-xs", getStatusColor(request.urgency))}>
              {request.urgency}
            </Badge>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            {formatDate(request.created_at)}
          </div>
          {request.job_reference && (
            <span className="text-xs text-slate-500">Job: {request.job_reference}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CreateRequestDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [formData, setFormData] = useState({
    part_id: '',
    part_description: '',
    quantity: 1,
    urgency: 'medium',
    reason: '',
    job_reference: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      partAPI.getAll().then(res => setParts(res.data.parts || []));
    }
  }, [open]);

  const handlePartSelect = (partId) => {
    const part = parts.find(p => p.id === partId);
    setFormData(prev => ({
      ...prev,
      part_id: partId,
      part_description: part ? `${part.part_number} - ${part.description}` : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.part_description || !formData.reason) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await requestAPI.create(formData);
      toast.success('Part request created successfully');
      setOpen(false);
      setFormData({
        part_id: '',
        part_description: '',
        quantity: 1,
        urgency: 'medium',
        reason: '',
        job_reference: '',
        notes: '',
      });
      onCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-request-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Part Request</DialogTitle>
          <DialogDescription>Request a part for a job or maintenance task</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Part (optional)</Label>
            <Select value={formData.part_id} onValueChange={handlePartSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select from catalog..." />
              </SelectTrigger>
              <SelectContent>
                {parts.map(part => (
                  <SelectItem key={part.id} value={part.id}>
                    {part.part_number} - {part.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="part_description">Part Description *</Label>
            <Input
              id="part_description"
              value={formData.part_description}
              onChange={(e) => setFormData(prev => ({ ...prev, part_description: e.target.value }))}
              placeholder="Describe the part needed"
              required
              data-testid="request-part-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                data-testid="request-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Urgency</Label>
              <Select value={formData.urgency} onValueChange={(v) => setFormData(prev => ({ ...prev, urgency: v }))}>
                <SelectTrigger data-testid="request-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Why is this part needed?"
              required
              data-testid="request-reason"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="job_reference">Job Reference (optional)</Label>
            <Input
              id="job_reference"
              value={formData.job_reference}
              onChange={(e) => setFormData(prev => ({ ...prev, job_reference: e.target.value }))}
              placeholder="WO-12345"
              data-testid="request-job-reference"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-request-btn">
              {loading ? 'Creating...' : 'Create Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function RequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (urgencyFilter !== 'all') params.urgency = urgencyFilter;
      
      const response = await requestAPI.getAll(params);
      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, urgencyFilter]);

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.request_number.toLowerCase().includes(query) ||
      req.part_description.toLowerCase().includes(query) ||
      req.requester_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6" data-testid="requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Part Requests</h1>
          <p className="text-slate-600">{total} total requests</p>
        </div>
        <CreateRequestDialog onCreated={fetchRequests} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search requests..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-requests"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="urgency-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {urgencyOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Request List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No requests found</h3>
            <p className="text-slate-500 mb-4">
              {searchQuery || statusFilter !== 'all' || urgencyFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first part request to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </div>
      )}
    </div>
  );
}
